import React from 'react';
import { Box, Divider, Grid, Typography } from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    Payment as PaymentIcon,
    AttachMoney as AttachMoneyIcon,
    AccountBalance as AccountBalanceIcon,
    Receipt as ReceiptIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
} from '@mui/icons-material';
import StatCard from './StatCard';
import { toPersianDigits } from '../../utils/convertNumbers';
import { formatJalaliDateTime } from '../../utils/formatJalaliDate';

/**
 * The single, shared financial KPI grid rendered identically by both
 * گزارش مالی (RevenueOverview) and the آمار مالی section of آمار سامانه.
 *
 * Fed by the /accounting/api/v1/revenue/overview/ response, so the two screens
 * cannot drift. Two visually separated groups (core KPIs only — no
 * payment-method breakdown):
 *
 * Windowed by the selected date range:
 *   درآمد کل = gross_revenue = Σ teacher_wage + Σ institute_portion over the
 *     windowed operations (frozen columns, never the current fee).
 *   سهم آموزگاران = teacher_wage (cost paid to teachers)
 *   هزینه‌های کل = expenses in window
 *   درآمد خالص = gross − teacher − expenses
 *
 * Point-in-time snapshot (backend ignores start/end date — see
 * docs/accounting-revenue.md "Outstanding debtors / prepaid balance are NOT
 * windowed"), rendered under a divider stamped with the current Jalali
 * date-time so the two groups can't be read as one period:
 *   مطالبات از هنرجویان (بدهی) = debtors (negative balances)
 *   پیش‌دریافت شهریه = prepaid, unconsumed sessions (positive balances)
 */
const formatCurrency = (amount) =>
    `${toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount || 0))} تومان`;

const SNAPSHOT_HINT = 'وضعیت لحظه‌ای — مستقل از بازهٔ انتخابی';

export default function FinancialOverviewCards({ data }) {
    if (!data) return null;

    const gross = data.gross_revenue || 0;
    const now = toPersianDigits(formatJalaliDateTime(new Date().toISOString()));

    const windowedCards = [
        { icon: TrendingUpIcon, color: 'success', label: 'درآمد کل (شهریه)', value: gross },
        { icon: PaymentIcon, color: 'warning', label: 'سهم آموزگاران', value: data.total_teacher_payouts },
        { icon: AttachMoneyIcon, color: 'error', label: 'هزینه‌های کل', value: data.total_expenses },
        {
            icon: AccountBalanceIcon,
            color: 'primary',
            label: 'درآمد خالص آموزشگاه',
            value: data.net_income,
            hint: '(درآمد کل − سهم آموزگاران − هزینه)',
        },
    ];

    const snapshotCards = [
        {
            icon: ReceiptIcon,
            color: 'error',
            label: 'مطالبات از هنرجویان (بدهی)',
            value: data.outstanding_debtors,
            hint: SNAPSHOT_HINT,
        },
        {
            icon: AccountBalanceWalletIcon,
            color: 'info',
            label: 'پیش‌دریافت شهریه (جلسات مانده)',
            value: data.prepaid_balance,
            hint: SNAPSHOT_HINT,
        },
    ];

    const renderCards = (cards) => (
        <Grid container spacing={2}>
            {cards.map((c) => (
                <Grid item xs={12} sm={6} md={4} key={c.label}>
                    <StatCard
                        icon={c.icon}
                        color={c.color}
                        label={c.label}
                        value={formatCurrency(c.value)}
                        hint={c.hint}
                    />
                </Grid>
            ))}
        </Grid>
    );

    return (
        <Box>
            {renderCards(windowedCards)}
            <Divider sx={{ my: 2 }}>
                <Typography variant="caption" color="text.secondary">
                    {`تراز جاری هنرجویان (تا ${now})`}
                </Typography>
            </Divider>
            {renderCards(snapshotCards)}
        </Box>
    );
}
